import { useEffect, useState } from "react";
import { Star, Users } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface FellowDifficultyRatingProps {
  mcqId: string;
}

const LABELS = ["Very easy", "Easy", "Moderate", "Hard", "Very hard"];

export const FellowDifficultyRating = ({ mcqId }: FellowDifficultyRatingProps) => {
  const { user } = useAuth();
  const [myRating, setMyRating] = useState<number | null>(null);
  const [hover, setHover] = useState<number | null>(null);
  const [avg, setAvg] = useState<number | null>(null);
  const [count, setCount] = useState(0);
  const [saving, setSaving] = useState(false);
  const [latestAttemptId, setLatestAttemptId] = useState<string | null>(null);

  // Load crowd average + this user's most recent attempt rating
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const { data: agg } = await supabase
        .from("attempts")
        .select("fellow_difficulty")
        .eq("mcq_id", mcqId)
        .not("fellow_difficulty", "is", null);

      if (!cancelled && agg) {
        const ratings = agg.map((a: any) => a.fellow_difficulty as number).filter(Boolean);
        if (ratings.length > 0) {
          setAvg(ratings.reduce((s, n) => s + n, 0) / ratings.length);
          setCount(ratings.length);
        } else {
          setAvg(null);
          setCount(0);
        }
      }

      if (user?.id) {
        const { data: mine } = await supabase
          .from("attempts")
          .select("id, fellow_difficulty")
          .eq("mcq_id", mcqId)
          .eq("user_id", user.id)
          .order("attempted_at", { ascending: false })
          .limit(1)
          .maybeSingle();

        if (!cancelled && mine) {
          setLatestAttemptId(mine.id);
          setMyRating((mine.fellow_difficulty as number | null) ?? null);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [mcqId, user?.id]);

  const submitRating = async (value: number) => {
    if (!user?.id || !latestAttemptId || saving) return;
    setSaving(true);
    const previous = myRating;
    setMyRating(value);

    const { error } = await supabase
      .from("attempts")
      .update({ fellow_difficulty: value })
      .eq("id", latestAttemptId)
      .eq("user_id", user.id);

    if (error) {
      setMyRating(previous);
      toast.error("Could not save rating");
    } else {
      toast.success(`Rated as ${LABELS[value - 1].toLowerCase()}`);
      // Optimistic refresh of average
      const { data: agg } = await supabase
        .from("attempts")
        .select("fellow_difficulty")
        .eq("mcq_id", mcqId)
        .not("fellow_difficulty", "is", null);
      if (agg) {
        const ratings = agg.map((a: any) => a.fellow_difficulty as number).filter(Boolean);
        setAvg(ratings.length ? ratings.reduce((s, n) => s + n, 0) / ratings.length : null);
        setCount(ratings.length);
      }
    }
    setSaving(false);
  };

  if (!user?.id || !latestAttemptId) {
    // No attempt to attach the rating to (e.g. review-only view) — show crowd avg only if available
    if (avg === null) return null;
    return (
      <div className="rounded-lg border bg-muted/20 p-3 flex items-center gap-2 text-sm text-muted-foreground">
        <Users className="h-4 w-4" />
        <span>
          Fellow-rated difficulty: <strong className="text-foreground">{avg.toFixed(1)}</strong> / 5
          <span className="ml-1 opacity-70">({count} {count === 1 ? "rating" : "ratings"})</span>
        </span>
      </div>
    );
  }

  const display = hover ?? myRating ?? 0;

  return (
    <div className="rounded-lg border bg-muted/20 p-3 space-y-2">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm font-medium">
          {myRating ? "Your difficulty rating" : "How hard was this for you?"}
        </div>
        {avg !== null && (
          <div className="text-xs text-muted-foreground flex items-center gap-1">
            <Users className="h-3 w-3" />
            Avg <strong className="text-foreground">{avg.toFixed(1)}</strong>/5
            <span className="opacity-70">({count})</span>
          </div>
        )}
      </div>
      <div className="flex items-center gap-1" onMouseLeave={() => setHover(null)}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            disabled={saving}
            onMouseEnter={() => setHover(n)}
            onClick={() => submitRating(n)}
            className={cn(
              "p-1 rounded transition-transform hover:scale-110 disabled:opacity-50",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            )}
            aria-label={`Rate ${n} of 5: ${LABELS[n - 1]}`}
          >
            <Star
              className={cn(
                "h-5 w-5 transition-colors",
                n <= display
                  ? "fill-yellow-400 text-yellow-400"
                  : "text-muted-foreground/40"
              )}
            />
          </button>
        ))}
        <span className="ml-2 text-xs text-muted-foreground min-w-[80px]">
          {display > 0 ? LABELS[display - 1] : "Tap to rate"}
        </span>
      </div>
    </div>
  );
};
